/**
 * 打ち合わせ（Meet）フック。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件の打ち合わせ（作成・一覧・詳細）をTanStack Query経由で読み書きする
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { meetingApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { CreateMeetingInput, MeetingTag } from '../lib/api/meeting.js';

/**
 * 案件の打ち合わせ一覧を取得する。tags を渡すと、そのいずれかのタグを持つ打ち合わせだけに
 * 絞り込む（絞り込み条件ごとに別キャッシュとして保持する）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件の打ち合わせ一覧を取得する（タグによる絞り込み対応）
 * @graph-connects /projects/:pjKey/meetings [calls] 打ち合わせ一覧取得API
 * @graph-connects meetingApi.list [calls] 打ち合わせ一覧APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMeetings(pjKey: string, tags?: MeetingTag[]) {
  return useQuery({
    queryKey: queryKeys.meetings.list(pjKey, tags ?? []),
    queryFn: () => meetingApi.list(pjKey, tags),
    enabled: pjKey.length > 0,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 打ち合わせ1件の文字起こし・議事録を取得する
 * @graph-connects /projects/:pjKey/meetings/:meetingId [calls] 打ち合わせ詳細取得API
 * @graph-connects meetingApi.detail [calls] 打ち合わせ詳細APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useMeetingDetail(pjKey: string, meetingId: string | null) {
  return useQuery({
    queryKey: queryKeys.meetings.detail(pjKey, meetingId ?? ''),
    queryFn: () => {
      if (meetingId === null) throw new Error('meetingId is required');
      return meetingApi.detail(pjKey, meetingId);
    },
    enabled: meetingId !== null,
  });
}

/**
 * 打ち合わせを作成する。
 *
 * 作成後は一覧を読み直す（Slack bot・Chrome拡張経由で既に作られた打ち合わせと
 * 同じ一覧に並ぶため、作成直後から過去分と区別なく見える）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件のSlackチャンネルに紐づく打ち合わせを作成し、一覧を再取得する
 * @graph-connects /projects/:pjKey/meetings [calls] 打ち合わせ作成API
 * @graph-connects meetingApi.create [calls] 打ち合わせ作成APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useCreateMeeting(pjKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMeetingInput = {}) => meetingApi.create(pjKey, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list(pjKey) });
    },
  });
}

/**
 * 打ち合わせの種類タグを上書きする（Gemini自動分類の結果を人手で上書きする用途）。
 * 更新後は一覧を読み直す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 打ち合わせの種類タグを上書きし、一覧を再取得する
 * @graph-connects /projects/:pjKey/meetings/:meetingId/tags [calls] 打ち合わせタグ更新API
 * @graph-connects meetingApi.updateTags [calls] 打ち合わせタグ更新APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useUpdateMeetingTags(pjKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ meetingId, tags }: { meetingId: string; tags: MeetingTag[] }) =>
      meetingApi.updateTags(pjKey, meetingId, tags),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.meetings.list(pjKey) });
    },
  });
}
